/**
 * 会員一覧の「レンタルステータス」「チケット保有」絞り込みの共有定義。
 * API（apps/api/ac-mens-hub）と Web（apps/web/ac-mens-hub）が共有する。
 *
 * 表示文言を両側で独立に持つと、画面の選択肢と API が返す値がズレて
 * 「選んでも 0 件」という壊れ方をする。文言は1箇所に置き、両側から import する。
 *
 * 文言の出どころは2系統ある。
 * - メンズ側 Read Model（mens-mongo `orders.ui_status_label`）がお客様画面に出す文言
 * - Phoenix 側 `order_status` から引く文言（メンズ側に行が無いオーダー用）
 * 前者は上流が持つ値なのでこのファイルで列挙するしかない。上流が文言を増減させたら
 * ここも追随する必要がある（選択肢に無い文言は絞り込めなくなる）。
 *
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Shares the member list rental status and ticket holding filter values between the API query validation and the Web filter UI so both sides cannot drift.
 * @graph-connects none
 */
/**
 * レンタルステータスの表示文言。
 *
 * `canceled` は Phoenix 側 `order_status=15` とメンズ側 `order_status=14` の
 * どちらでも同じ文言に寄せる。両方を別文言で出すと、同じ「取り消された注文」が
 * 選択肢に2つ並んで利用者がどちらを選べばよいか判断できなくなる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Maps each member list rental status to the single label shown in both the filter options and the table column.
 * @graph-connects none
 */
export const RENTAL_STATUS_LABELS = {
    noOrder: 'オーダーなし',
    request: 'リクエスト',
    styling: 'スタイリング中',
    stylingConfirm: 'スタイリング内容確認',
    stylingCompleted: 'スタイリング完了',
    preparing: '出荷準備中',
    shipping: '配送中',
    awaitingReceipt: '受け取り待ち',
    renting: 'レンタル中',
    returnRequested: '返送手続き済み',
    returnChecking: '返却確認中',
    delivered: '配送完了',
    canceled: 'キャンセル',
};
/**
 * 絞り込みの選択肢。申込 → スタイリング → 出荷 → レンタル → 返却 → 完了 の業務順に並べる。
 *
 * 件数順にしないのは、探す人が業務の流れで対象を思い浮かべるため。
 * 本番実測で現在 0 人の `returnRequested` / `returnChecking` も残す。状態としては実在し、
 * 一時的に滞留していないだけなので、外すと出現した瞬間に絞り込めなくなる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Lists the member list rental status filter options in business flow order for the filter UI and the API validation.
 * @graph-connects none
 */
export const RENTAL_STATUS_OPTIONS = [
    RENTAL_STATUS_LABELS.noOrder,
    RENTAL_STATUS_LABELS.request,
    RENTAL_STATUS_LABELS.styling,
    RENTAL_STATUS_LABELS.stylingConfirm,
    RENTAL_STATUS_LABELS.stylingCompleted,
    RENTAL_STATUS_LABELS.preparing,
    RENTAL_STATUS_LABELS.shipping,
    RENTAL_STATUS_LABELS.awaitingReceipt,
    RENTAL_STATUS_LABELS.renting,
    RENTAL_STATUS_LABELS.returnRequested,
    RENTAL_STATUS_LABELS.returnChecking,
    RENTAL_STATUS_LABELS.delivered,
    RENTAL_STATUS_LABELS.canceled,
];
/**
 * プラン絞り込みの選択肢。
 *
 * 値は `aircloset_mens.subscription_plans.name` の実データと一致していなければならない。
 * 一致しない文言を並べても該当者が0件になるだけで、画面側にエラーは出ない。
 * プランが増減したらここも更新する（列には実データのプラン名がそのまま出るため、
 * 選択肢に無いプランがあれば一覧を見れば気付ける）。
 *
 * 2026-08-25 時点の本番実データ（有効な契約数）:
 * 単月契約かつ2ヶ月に1回交換 741 / 年間契約かつ2ヶ月に1回交換 249 /
 * 単月契約かつ1ヶ月に1回交換 59 / 年間契約かつ1ヶ月に1回交換 65
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Lists the subscription plan names offered as member list filter options, matching the plan master in the mens database.
 * @graph-connects none
 */
export const MEMBER_PLAN_NAMES = [
    '単月契約かつ2ヶ月に1回交換',
    '年間契約かつ2ヶ月に1回交換',
    '単月契約かつ1ヶ月に1回交換',
    '年間契約かつ1ヶ月に1回交換',
];
/**
 * チケット保有の絞り込み値。
 *
 * 枚数そのものではなく「持っているかどうか」で絞る。本番実測では 0 枚が 6,525 人／
 * 1 枚以上が 887 人で、実務上この2値で足りる。実際の枚数は一覧の列に出すため、
 * 2 枚以上を探したい場合は列を見て選べる。
 *
 * @graph-node {Variable}
 * @graph-stack ac-mens-hub
 * @graph-domain Operations
 * @graph-business Lists the ticket holding filter values shared by the API query validation and the Web filter UI.
 * @graph-connects none
 */
export const TICKET_HOLDING_VALUES = ['none', 'some'];
//# sourceMappingURL=member-list-filters.js.map